import React, { useState } from 'react';
import './Terms.css';

const sections = [
  {
    title: '1. Introduction',
    content: `Welcome to K2 NEET, an AI-powered educational assistant operated by K2Kedar Infotech LLP, an Indian entity. These Terms of Service ("Terms") govern your access to and use of the K2 NEET mobile application (the "App") and associated services including AI-powered chat responses, summaries, notes, and Q&A features (collectively, the "Services").

By accessing or using K2 NEET, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not use the Services.`
  },
  {
    title: '2. Acceptance of Terms',
    content: `By registering for, accessing, or using the Services in any manner, you agree to abide by these Terms and any applicable local laws or regulations. If you are accepting these Terms on behalf of a legal entity (such as a school or organization), you represent and warrant that you have the authority to do so.`
  },
  {
    title: '3. Eligibility',
    content: `You must be at least 13 years old to use the Services. If you are under 18, you must have parental or legal guardian consent. By using the app, you confirm that you meet these eligibility requirements.`
  },
  {
    title: '4. Description of Services',
    content: `K2 NEET provides an educational platform designed to help students prepare for the NEET-UG examination using AI tools. The Services include:
• AI-Powered Q&A: Ask NEET-related questions and receive answers powered by OpenAI's GPT technology.
• Summaries: Get concise summaries based on standard NEET curriculum topics.
• Notes: AI-assisted generation of revision-ready notes tailored to user input.

The Services are for informational and educational purposes only. K2 NEET does not guarantee accuracy, completeness, or suitability for examination success. The App is intended solely for academic support and is not affiliated with the National Testing Agency (NTA) or any official NEET body.`
  },
  {
    title: '5. User Accounts',
    content: `To access the Services, you must register using a valid mobile number and name. By creating an account:
• You agree to provide accurate and complete information.
• You are responsible for maintaining the confidentiality of your login credentials.
• You agree to notify us immediately of any unauthorized use of your account.

K2 NEET reserves the right to suspend or terminate accounts that violate these Terms or engage in misuse of the Services.`
  },
  {
    title: '6. AI-Generated Content Disclaimer',
    content: `K2 NEET utilizes OpenAI's large language model to generate responses. AI-generated content is:
• Generated automatically based on user input and trained patterns.
• Not a substitute for verified medical or academic advice.
• Not guaranteed to be factually correct or aligned with official syllabus interpretations.

Use discretion when relying on AI responses and consult your textbooks, teachers, or official sources for clarification.`
  },
  {
    title: '7. Acceptable Use Policy',
    content: `You agree to use K2 NEET strictly for educational purposes. You must not:
• Use the app to generate, share, or store any harmful, abusive, unlawful, or inappropriate content.
• Attempt to exploit, reverse-engineer, or interfere with the AI systems or platform infrastructure.
• Use automation, bots, or scraping techniques to access or misuse the Services.
• Misrepresent your identity or violate intellectual property laws.

Any misuse of the app may result in suspension or legal action as applicable under Indian law.`
  },
  {
    title: '8. Intellectual Property',
    content: `All content, features, and functionality on K2 NEET — including but not limited to text, graphics, icons, software, and AI-generated outputs — are the property of K2Kedar Infotech LLP or its licensors (e.g., OpenAI) and are protected under applicable intellectual property laws.

You may use the app solely for personal, non-commercial, educational purposes. You may not copy, distribute, modify, or create derivative works from any content unless explicitly permitted.`
  },
  {
    title: '9. Privacy',
    content: `Your privacy is important to us. Please refer to our Privacy Policy to understand how we collect, use, and protect your personal information. By using the Services, you agree to the collection and use of your information as described therein.`
  },
  {
    title: '10. Third-Party Services',
    content: `K2 NEET may integrate or rely on third-party services (e.g., OpenAI for AI responses, Razorpay for payments). Your use of these services is subject to their respective terms and policies. We are not responsible for the accuracy, reliability, or safety of third-party services.`
  },
  {
    title: '11. Termination',
    content: `We reserve the right to suspend or terminate your access to the Services at any time, without notice, for:
• Breach of these Terms
• Misuse of the platform
• Technical or security concerns
• Compliance with legal or regulatory requirements

Upon termination, your right to use the Services will immediately cease. Stored content may be retained or deleted in accordance with our Privacy Policy and internal data policies.`
  },
  {
    title: '12. Disclaimers',
    content: `The Services provided through K2 NEET are offered "as is" and "as available." We do not warrant that:
• The AI-generated content will be accurate, reliable, or suitable for every educational use.
• The Services will be uninterrupted, secure, or error-free.
• Any data, including chat history or notes, will be permanently preserved.

You use the Services at your own discretion and risk. K2 NEET is a study aid — it is not a replacement for official coaching, study materials, or certified educational professionals.`
  },
  {
    title: '13. Limitation of Liability',
    content: `To the fullest extent permitted under applicable law, K2Kedar Infotech LLP, its directors, employees, partners, affiliates, and licensors shall not be liable for:
• Any indirect, incidental, special, consequential, or punitive damages;
• Any loss of data, profits, or business interruptions;
• Any damages arising from your use or inability to use the Services.

Your sole remedy for dissatisfaction with the Services is to discontinue use of the app.`
  },
  {
    title: '14. Governing Law',
    content: `These Terms shall be governed and construed in accordance with the laws of India, with jurisdiction falling under the courts of India. Any disputes arising in connection with these Terms will be subject to the exclusive jurisdiction of Indian courts.`
  },
  {
    title: '15. Contact Information',
    content: `If you have questions about these Terms, please contact us at:

📧 support@k2neet.com`
  }
];

export default function Terms() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <div className="terms-container">
      <div className="terms-card">
        <h1>Terms of Service</h1>
        <h2>Effective Date: 25th June 2025</h2>
        <p>
          These Terms of Service govern your access to and use of the K2 NEET mobile application and associated services. 
          By accessing or using K2 NEET, you agree to be bound by these Terms and our Privacy Policy.
        </p>
      </div>
      <div className="terms-accordion-section">
        {sections.map((section, idx) => (
          <div key={section.title} className="accordion-item">
            <button className="accordion-title" onClick={() => toggleAccordion(idx)}>
              {section.title}
              <span className="accordion-arrow">{openIndex === idx ? '▲' : '▼'}</span>
            </button>
            {openIndex === idx && (
              <div className="accordion-content">{section.content}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
} 